import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, Users, Target,
  TrendingUp, Rocket, Shield, BarChart3, Sparkles, Award, Building2,
  Globe, Calendar, Briefcase, BookOpen, MessageSquare, DollarSign,
  Mic, Zap, FileText, Handshake, CheckCircle, Menu
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const SLIDES = [
  // Slide 0 — Title
  {
    id: "title",
    render: (nav) => (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-16 w-auto mb-6" />
        <h1 className="text-5xl sm:text-6xl font-bold text-white mb-2">My Biz Consulting</h1>
        <p className="text-sm text-[#39FF14] uppercase tracking-[0.3em] mb-10">Business Solutions Center</p>
        <div className="w-16 h-0.5 bg-[#39FF14] mb-10" />
        <h2 className="text-2xl sm:text-3xl text-white font-light mb-3">Strategic Sponsorship Opportunities</h2>
        <p className="text-xl text-[#39FF14] font-semibold mb-8">$25K – $250K Partnerships</p>
        <p className="text-neutral-400 italic max-w-xl">Empowering entrepreneurs through AI-powered business growth systems.</p>
      </div>
    )
  },
  // Slide 1 — The Problem
  {
    id: "problem",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-8">The Problem</h2>
        <p className="text-lg text-neutral-300 mb-6">Millions of small businesses struggle to scale because they lack:</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {[
            { icon: Target, text: "Strategic guidance" },
            { icon: BarChart3, text: "Operational systems" },
            { icon: TrendingUp, text: "Growth infrastructure" },
            { icon: Users, text: "Meaningful networks" },
            { icon: DollarSign, text: "Capital readiness" }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
              <item.icon className="w-6 h-6 text-[#39FF14] flex-shrink-0" />
              <span className="text-white font-medium">{item.text}</span>
            </div>
          ))}
        </div>
        <p className="text-neutral-400 mb-8">Most founders operate in <span className="text-[#39FF14] font-semibold">business chaos</span> rather than scalable systems.</p>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
          <p className="text-red-400 font-semibold mb-3">As a result:</p>
          {["Businesses plateau early and lose momentum", "Growth opportunities are missed", "Economic potential goes unrealized"].map((t, i) => (
            <p key={i} className="text-neutral-300 text-sm flex items-center gap-2 mb-2"><span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />{t}</p>
          ))}
        </div>
      </div>
    )
  },
  // Slide 2 — Our Solution
  {
    id: "solution",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-4">Our Solution</h2>
        <p className="text-lg text-neutral-300 mb-8">
          The <span className="text-[#39FF14] font-semibold">Business Solutions Center (BSC)</span> is an AI-powered business growth ecosystem designed to transform entrepreneurs from founders into scalable business leaders.
        </p>
        <p className="text-[#39FF14] font-semibold mb-4">Our platform combines:</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Sparkles, title: "AI Business Diagnostics", desc: "Clarity assessments with personalized growth roadmaps" },
            { icon: Briefcase, title: "Strategic Consulting", desc: "R.A.P.I.D. framework for structured business transformation" },
            { icon: BookOpen, title: "Entrepreneur Education", desc: "Programs, tools, and training for every growth stage" },
            { icon: Users, title: "Business Community", desc: "Networking, mastermind pods, and opportunity marketplace" },
            { icon: BarChart3, title: "Growth Infrastructure", desc: "SOPs, KPIs, business plans, and marketing strategy tools" },
            { icon: Shield, title: "Impact Reporting", desc: "Professional accountability reports for funders and stakeholders" }
          ].map((item, i) => (
            <div key={i} className="p-5 bg-white/5 rounded-xl border border-white/10">
              <item.icon className="w-7 h-7 text-[#39FF14] mb-3" />
              <h3 className="text-white font-semibold mb-1">{item.title}</h3>
              <p className="text-sm text-neutral-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },
  // Slide 3 — Platform Ecosystem
  {
    id: "platform",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-3">The Platform Ecosystem</h2>
        <p className="text-neutral-400 mb-8">An integrated growth environment for entrepreneurs</p>
        <div className="mb-8">
          <h3 className="text-[#39FF14] font-semibold text-lg mb-4">AI Business Diagnostics</h3>
          <p className="text-neutral-300 mb-3">Entrepreneurs complete a Business Clarity Assessment that generates:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {["Clarity Score (1-100)", "Strategic Insights", "Priority Focus Areas", "90-Day Roadmap"].map((t, i) => (
              <div key={i} className="p-3 bg-[#39FF14]/10 rounded-lg text-center">
                <p className="text-sm text-white font-medium">{t}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-[#39FF14] font-semibold text-lg mb-4">AI Scaling Tools</h3>
          <div className="grid grid-cols-3 gap-3">
            {["SOP Generator", "Business Plan Builder", "KPI Scorecard", "Org Structure Map", "Marketing Strategy", "Implementation Planner", "Time Budget Optimizer", "Tech Stack Advisor", "Brand Guidelines"].map((t, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-lg border border-white/10 text-center">
                <p className="text-sm text-neutral-300">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  // Slide 4 — Community
  {
    id: "community",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-3">Entrepreneur Community Ecosystem</h2>
        <p className="text-neutral-300 mb-8">The <span className="text-[#39FF14]">My Biz Strategic Alliance</span> connects founders with opportunities and resources.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {[
            { icon: Users, title: "Networking Channels", desc: "Partnerships, referrals, hiring, funding" },
            { icon: Briefcase, title: "Opportunity Marketplace", desc: "Post and discover business opportunities" },
            { icon: Target, title: "Mastermind Pods", desc: "7 curated groups by industry and stage" },
            { icon: MessageSquare, title: "Direct Messaging", desc: "Member-to-member communication" },
            { icon: BookOpen, title: "Resource Vault", desc: "Templates, guides, training across 8 categories" },
            { icon: Award, title: "Gamification", desc: "Points, badges, leaderboard for engagement" }
          ].map((item, i) => (
            <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10">
              <item.icon className="w-6 h-6 text-[#39FF14] mb-2" />
              <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
              <p className="text-xs text-neutral-400">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#39FF14]/10 border border-[#39FF14]/20 rounded-xl p-4 text-center">
          <p className="text-white font-medium">Sponsors gain <span className="text-[#39FF14]">direct brand presence</span> within this entrepreneurial ecosystem.</p>
        </div>
      </div>
    )
  },
  // Slide 5 — Programs
  {
    id: "programs",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-3">Programs That Drive Growth</h2>
        <p className="text-neutral-400 mb-8">From idea to growth to scale</p>
        <div className="space-y-6">
          <div className="p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <Zap className="w-7 h-7 text-[#39FF14]" />
              <h3 className="text-xl font-bold text-white">R.A.P.I.D. Business Recalibration</h3>
              <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30">$1,250–$2,500</Badge>
            </div>
            {["Identify operational bottlenecks holding back growth", "Rebuild business systems with proven frameworks", "Implement scalable strategy with 30/60/90 day milestones"].map((t, i) => (
              <p key={i} className="text-neutral-300 text-sm flex items-center gap-2 mb-1.5"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />{t}</p>
            ))}
          </div>
          <div className="p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <Mic className="w-7 h-7 text-[#39FF14]" />
              <h3 className="text-xl font-bold text-white">Podcast Launch Academy</h3>
              <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30">$750–$1,000</Badge>
            </div>
            <p className="text-neutral-300 text-sm">12-week program helping entrepreneurs build authority, media visibility, and thought leadership.</p>
          </div>
          <div className="p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-7 h-7 text-[#39FF14]" />
              <h3 className="text-xl font-bold text-white">Project-Based Services</h3>
              <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30">From $1,200/mo</Badge>
            </div>
            <p className="text-neutral-300 text-sm">24 services across Marketing, AI Visibility, Branding, and Business Services. Plus Global Remote Staffing with 9 role categories.</p>
          </div>
        </div>
      </div>
    )
  },
  // Slide 6 — Growth Model
  {
    id: "growth-model",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-3">Our Growth Model</h2>
        <p className="text-neutral-400 mb-8">A structured lead-to-client growth funnel</p>
        <div className="space-y-3">
          {[
            { step: "01", title: "Business Clarity Assessment", desc: "Free AI-powered diagnostic — captures leads", color: "from-[#39FF14]/20 to-transparent" },
            { step: "02", title: "AI Business Insights", desc: "Personalized clarity score, roadmap, and action plan", color: "from-[#39FF14]/15 to-transparent" },
            { step: "03", title: "Strategic Consultation", desc: "Consultant-led guidance and program recommendation", color: "from-[#39FF14]/12 to-transparent" },
            { step: "04", title: "Client Onboarding", desc: "NDA, intake form, dashboard access", color: "from-[#39FF14]/10 to-transparent" },
            { step: "05", title: "Scaling Tools & Programs", desc: "R.A.P.I.D., AI tools, project services", color: "from-[#39FF14]/8 to-transparent" },
            { step: "06", title: "Community Collaboration", desc: "Networking, pods, opportunities, resources", color: "from-[#39FF14]/5 to-transparent" }
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r ${item.color} border border-white/10`}>
              <span className="text-3xl font-black text-[#39FF14]/30 w-12 text-center">{item.step}</span>
              <div>
                <h3 className="text-white font-semibold">{item.title}</h3>
                <p className="text-sm text-neutral-400">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  // Slide 7 — Why Sponsors
  {
    id: "why-sponsors",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-8">Why Sponsors Partner With Us</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { icon: Globe, title: "Support Economic Development", desc: "Help entrepreneurs build stronger businesses and create jobs in communities." },
            { icon: Users, title: "Engage Directly With Entrepreneurs", desc: "Connect with founders, startups, and growing companies through a living platform." },
            { icon: Award, title: "Strengthen Brand Leadership", desc: "Position your organization as a champion of entrepreneurship and innovation." },
            { icon: Sparkles, title: "Support Innovation", desc: "Align with an AI-powered business development platform at the forefront of tech." },
            { icon: Building2, title: "Co-Branded Sponsor Portal", desc: "Get your own branded portal page where your entrepreneurs register and are tracked." },
            { icon: FileText, title: "Impact & Accountability Reports", desc: "Receive professional PDF reports documenting real outcomes and engagement." }
          ].map((item, i) => (
            <div key={i} className="p-5 bg-white/5 rounded-xl border border-white/10">
              <item.icon className="w-8 h-8 text-[#39FF14] mb-3" />
              <h3 className="text-white font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-neutral-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },
  // Slide 8 — Sponsor Visibility
  {
    id: "visibility",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-3">Sponsor Visibility</h2>
        <p className="text-neutral-400 mb-8">Multi-channel exposure across the BSC ecosystem</p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { title: "Platform Visibility", items: ["Logo placement across BSC platform", "Featured recognition on partner pages", "Exposure inside entrepreneur community", "Co-branded sponsor portal"] },
            { title: "Community Engagement", items: ["Sponsored workshops", "Sponsored entrepreneur challenges", "Featured resources and tools", "Community event sponsorship"] },
            { title: "Thought Leadership", items: ["Co-hosted events", "Expert interviews", "Sponsor-led training sessions", "Newsletter and digest features"] }
          ].map((col, i) => (
            <div key={i} className="p-5 bg-white/5 rounded-xl border border-white/10">
              <h3 className="text-[#39FF14] font-semibold mb-4">{col.title}</h3>
              {col.items.map((item, j) => (
                <p key={j} className="text-sm text-neutral-300 flex items-center gap-2 mb-2"><CheckCircle className="w-3.5 h-3.5 text-[#39FF14] flex-shrink-0" />{item}</p>
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  },
  // Slide 9 — Sponsorship Tiers
  {
    id: "tiers",
    render: () => (
      <div className="max-w-5xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-8">Sponsorship Tiers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: "Strategic Partner", price: "$250,000", tag: "Flagship", features: ["Primary sponsor recognition", "Exclusive branding placement", "Sponsored entrepreneur programs", "Custom initiatives and reports", "Speaking opportunities"], highlight: true },
            { name: "Innovation Partner", price: "$100,000", tag: "Featured", features: ["Featured platform sponsor", "Community and event branding", "Thought leadership features", "Sponsored workshops"] },
            { name: "Growth Partner", price: "$50,000", tag: "Program", features: ["Sponsor recognition across programs", "Platform branding", "Event sponsorship opportunities"] },
            { name: "Community Partner", price: "$25,000", tag: "Entry", features: ["Sponsor listing", "Newsletter recognition", "Community visibility"] }
          ].map((tier, i) => (
            <div key={i} className={`p-5 rounded-xl border ${tier.highlight ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 bg-white/5"}`}>
              <Badge className={tier.highlight ? "bg-[#39FF14] text-black mb-2" : "bg-white/10 text-neutral-400 mb-2"}>{tier.tag}</Badge>
              <h3 className="text-white font-bold text-lg">{tier.name}</h3>
              <p className="text-[#39FF14] text-2xl font-bold mb-4">{tier.price}</p>
              {tier.features.map((f, j) => (
                <p key={j} className="text-xs text-neutral-300 flex items-center gap-2 mb-1.5"><CheckCircle className="w-3 h-3 text-[#39FF14] flex-shrink-0" />{f}</p>
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  },
  // Slide 10 — Impact Goals
  {
    id: "impact",
    render: () => (
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-white mb-8">Impact Goals</h2>
        <p className="text-neutral-300 mb-8">With strategic sponsorship, the Business Solutions Center will:</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {[
            { num: "1,000+", label: "Entrepreneurs supported annually" },
            { num: "9", label: "AI-powered scaling tools" },
            { num: "20+", label: "Automated email workflows" },
            { num: "13", label: "Section impact reports per participant" }
          ].map((s, i) => (
            <div key={i} className="p-5 bg-white/5 rounded-xl border border-white/10 text-center">
              <p className="text-3xl font-black text-[#39FF14]">{s.num}</p>
              <p className="text-xs text-neutral-400 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="text-[#39FF14] font-semibold mb-3">Provide access to:</p>
        {["AI business strategy tools and diagnostics", "Consultant-led guidance via R.A.P.I.D. framework", "Business development programs and education", "Networking, mastermind pods, and opportunity marketplace", "Project services, remote staffing, and growth infrastructure"].map((t, i) => (
          <p key={i} className="text-neutral-300 text-sm flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />{t}</p>
        ))}
      </div>
    )
  },
  // Slide 11 — Partnership Invitation
  {
    id: "invitation",
    render: (nav) => (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Partnership Invitation</h2>
        <p className="text-lg text-neutral-300 max-w-2xl mb-8">
          We are seeking forward-thinking organizations that believe in the power of entrepreneurship.
        </p>
        <p className="text-[#39FF14] font-semibold text-xl mb-6">Together we can:</p>
        {["Empower business leaders with AI-powered tools and strategic guidance", "Strengthen local economies through scalable business development", "Create lasting opportunities for entrepreneurs in every community"].map((t, i) => (
          <p key={i} className="text-white text-lg mb-3">{t}</p>
        ))}
        <div className="w-16 h-0.5 bg-[#39FF14] my-8" />
        <p className="text-[#39FF14] font-bold text-2xl mb-6">Become a Strategic Partner</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button size="lg" className="bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold h-14 px-8" onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}>
            <Calendar className="w-5 h-5 mr-2" /> Schedule a Conversation
          </Button>
          <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-14 px-8" onClick={() => nav("/partnership")}>
            <Handshake className="w-5 h-5 mr-2" /> Partnership Inquiry
          </Button>
        </div>
        <p className="text-neutral-500 text-sm mt-8 italic">Let's build the future of entrepreneurship together.</p>
        <p className="text-neutral-600 text-xs mt-4">partnerships@mybizconsulting.net | mybizconsulting.net/partnership</p>
      </div>
    )
  }
];

const PitchDeck = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const goNext = () => setCurrentSlide(s => Math.min(s + 1, SLIDES.length - 1));
  const goPrev = () => setCurrentSlide(s => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col">
      {/* Top Nav */}
      <nav className="border-b border-neutral-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <Link to="/" className="flex items-center gap-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-8 w-auto" />
          <div>
            <p className="text-xs font-semibold text-white leading-tight">My Biz Consulting</p>
            <p className="text-[8px] text-[#39FF14] uppercase tracking-widest">Business Solutions Center</p>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-500 hidden sm:inline">Slide {currentSlide + 1} of {SLIDES.length}</span>
          <Button size="sm" variant="outline" className="border-neutral-700 text-neutral-400 hover:text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </nav>

      {/* Slide Content */}
      <div className="flex-1 flex items-center justify-center py-8 overflow-y-auto">
        <div className="w-full max-w-6xl">
          {SLIDES[currentSlide].render(navigate)}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="border-t border-neutral-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <Button size="sm" variant="ghost" onClick={goPrev} disabled={currentSlide === 0} className="text-neutral-400 hover:text-white disabled:opacity-30">
          <ChevronLeft className="w-5 h-5 mr-1" /> Previous
        </Button>
        <div className="flex gap-1">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? "bg-[#39FF14] w-6" : "bg-neutral-700 hover:bg-neutral-500"}`} />
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={goNext} disabled={currentSlide === SLIDES.length - 1} className="text-neutral-400 hover:text-white disabled:opacity-30">
          Next <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default PitchDeck;
